import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientsAPI, miniPellesAPI, optionsAPI, devisAPI, categoriesAPI, cabinetsSNCAPI, serviceCatalogAPI } from '@/lib/api';
import { formatPrice, cn, getImageUrl } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Package,
  Users,
  Wrench,
  Loader2,
  Calculator,
  Building2,
  LayoutGrid,
  List,
  Check,
  ChevronsUpDown,
  Banknote,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ServiceQuoteEditor, createEmptyServiceLine, normalizeServiceLines } from '@/pages/ServiceQuoteShared';

const DevisEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [machineViewMode, setMachineViewMode] = useState('dropdown'); // 'dropdown', 'grid' or 'list' - default to dropdown
  
  const [clients, setClients] = useState([]);
  const [miniPelles, setMiniPelles] = useState([]);
  const [options, setOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cabinetsSNC, setCabinetsSNC] = useState([]);
  const [banques, setBanques] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [originalDevis, setOriginalDevis] = useState(null);
  
  // Brand filter
  const [selectedBrand, setSelectedBrand] = useState('all');
  
  const [form, setForm] = useState({
    devis_type: 'machine',
    client_id: '',
    mini_pelle_id: '',
    selected_variant_id: '', // ID de la variante sélectionnée
    selected_variant_price: null,
    selected_options: [],
    discount: 0,
    discount_type: 'percent',
    notes: '',
    validity_days: 30,
    show_snc: true,
    show_financement: false,
    cabinet_snc_id: '',
    banques: [], // Selected banques
    service_lines: [createEmptyServiceLine()],
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [devisRes, clientsRes, mpRes, optRes, catRes, cabRes, banquesRes, serviceCatalogRes] = await Promise.all([
        devisAPI.getOne(id),
        clientsAPI.getAll(),
        miniPellesAPI.getAll(),
        optionsAPI.getAll(),
        categoriesAPI.getAll(),
        cabinetsSNCAPI.getAll(),
        clientsAPI.getByContactType('banque').catch(() => ({ data: [] })),
        serviceCatalogAPI.getAll().catch(() => ({ data: [] })),
      ]);
      
      const devis = devisRes.data;
      setOriginalDevis(devis);
      setClients(clientsRes.data);
      setMiniPelles(mpRes.data);
      setOptions(optRes.data);
      setCategories(catRes.data);
      setCabinetsSNC(cabRes.data);
      setBanques(banquesRes.data || []);
      setServiceCatalog(serviceCatalogRes.data || []);
      
      // Populate form with existing devis data
      setForm({
        devis_type: devis.devis_type || 'machine',
        client_id: devis.client_id,
        mini_pelle_id: devis.mini_pelle_id,
        selected_variant_id: devis.selected_variant_id || '',
        selected_variant_price: devis.selected_variant_id ? (devis.mini_pelle_price || null) : null,
        selected_options: devis.options || [],
        discount: devis.discount || 0,
        discount_type: devis.discount_type || 'percent',
        notes: devis.notes || '',
        validity_days: devis.validity_days || 30,
        show_snc: devis.show_snc !== false,
        show_financement: Boolean(devis.show_financement || (devis.banques || []).length > 0),
        cabinet_snc_id: devis.cabinet_snc_id || '',
        banques: devis.banques || [],
        service_lines: normalizeServiceLines(devis.service_lines || []),
      });
    } catch (error) {
      toast.error('Erreur lors du chargement du devis');
      navigate('/admin/devis');
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const selectedMiniPelle = miniPelles.find((mp) => mp.id === form.mini_pelle_id);
  const [machinePopoverOpen, setMachinePopoverOpen] = useState(false);

  const formatTonnage = (mp) => {
    if (!mp.weight) return null;
    const w = String(mp.weight).replace(',', '.').trim();
    const num = parseFloat(w);
    if (isNaN(num)) return null;
    if (num < 10) return `${w.replace('.', ',')}T`;
    return `${num >= 1000 ? (num/1000).toFixed(1).replace('.', ',') + 'T' : num + ' kg'}`;
  };
  const selectedCabinetSNC = cabinetsSNC.find((c) => c.id === form.cabinet_snc_id);
  
  // Get selected variant if any
  const selectedVariant = selectedMiniPelle?.has_variants && form.selected_variant_id
    ? selectedMiniPelle.variants?.find(v => String(v.id) === String(form.selected_variant_id))
    : null;
  
  // Get effective price and image (variant overrides base)
  const effectivePrice = form.selected_variant_id
    ? ((form.selected_variant_price ?? selectedVariant?.price ?? selectedMiniPelle?.base_price) || 0)
    : (selectedMiniPelle?.base_price || 0);
  const effectiveImage = selectedVariant?.image_url || selectedMiniPelle?.image_url;
  const effectiveName = selectedVariant 
    ? `${selectedMiniPelle?.name} - ${selectedVariant.name}` 
    : selectedMiniPelle?.name;
  
  const getOptionPrice = (option) => {
    if (!form.mini_pelle_id) return 0;
    return option.prices_by_model?.[form.mini_pelle_id] || 0;
  };

  const toggleBanqueSelection = (banque) => {
    setForm((prev) => {
      const currentBanques = prev.banques || [];
      const isSelected = currentBanques.some((b) => b.id === banque.id);

      return {
        ...prev,
        banques: isSelected
          ? currentBanques.filter((b) => b.id !== banque.id)
          : [
              ...currentBanques,
              { id: banque.id, name: banque.name, email: banque.email },
            ],
      };
    });
  };

  const toggleOption = (option) => {
    const isSelected = form.selected_options.some((o) => o.option_id === option.id);
    
    if (isSelected) {
      setForm({
        ...form,
        selected_options: form.selected_options.filter((o) => o.option_id !== option.id),
      });
    } else {
      setForm({
        ...form,
        selected_options: [
          ...form.selected_options,
          {
            option_id: option.id,
            option_name: option.name,
            category: option.category,
            price: getOptionPrice(option),
            image_url: option.image_url,
          },
        ],
      });
    }
  };

  const calculateTotals = () => {
    const miniPellePrice = effectivePrice || 0;
    const optionsTotal = form.selected_options.reduce((sum, opt) => sum + opt.price, 0);
    const subtotal = miniPellePrice + optionsTotal;
    
    let discountAmount = 0;
    if (form.discount_type === 'percent') {
      discountAmount = subtotal * (form.discount / 100);
    } else {
      discountAmount = form.discount;
    }
    
    const total = subtotal - discountAmount;
    
    return { miniPellePrice, optionsTotal, subtotal, discountAmount, total };
  };

  const handleSubmit = async () => {
    if (!form.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    if (form.devis_type === 'service') {
      const serviceLines = normalizeServiceLines(form.service_lines).filter((line) => line.designation.trim());
      if (serviceLines.length === 0) {
        toast.error('Ajoute au moins une ligne de prestation ou de pièce');
        return;
      }

      setSubmitting(true);
      try {
        await devisAPI.update(id, {
          devis_type: 'service',
          client_id: form.client_id,
          client_name: selectedClient.name,
          client_email: selectedClient.email,
          client_phone: selectedClient.phone,
          service_lines: serviceLines,
          discount: form.discount,
          discount_type: form.discount_type,
          notes: form.notes,
          validity_days: form.validity_days,
          show_snc: false,
          show_financement: false,
          cabinet_snc_id: null,
          cabinet_snc_name: null,
          cabinet_snc_email: null,
          banques: [],
          apport_client_percent: 0,
        });
        toast.success('Devis prestation mis à jour');
        navigate(`/admin/devis/${id}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!form.mini_pelle_id) {
      toast.error('Veuillez sélectionner un client et une machine');
      return;
    }
    
    setSubmitting(true);
    
    // Préparer les specs de la machine
    const miniPelleSpecs = {
      weight: selectedMiniPelle.weight,
      longueur: selectedMiniPelle.longueur,
      largeur: selectedMiniPelle.largeur,
      profondeur_fouille: selectedMiniPelle.profondeur_fouille,
      hauteur_chargement: selectedMiniPelle.hauteur_chargement,
      marque_moteur: selectedMiniPelle.marque_moteur,
      nombre_cylindres: selectedMiniPelle.nombre_cylindres,
      puissance_moteur: selectedMiniPelle.puissance_moteur,
      marque_pompe: selectedMiniPelle.marque_pompe,
      debit_pompe: selectedMiniPelle.debit_pompe,
      brochure_url: selectedMiniPelle.brochure_url,
    };
    
    const devisData = {
      client_id: form.client_id,
      client_name: selectedClient.name,
      client_email: selectedClient.email,
      client_phone: selectedClient.phone,
      mini_pelle_id: form.mini_pelle_id,
      mini_pelle_name: effectiveName,
      mini_pelle_price: effectivePrice,
      mini_pelle_image: effectiveImage,
      mini_pelle_description: selectedMiniPelle.description,
      mini_pelle_specs: miniPelleSpecs,
      selected_variant_id: form.selected_variant_id || null,
      selected_variant_name: selectedVariant?.name || null,
      options: form.selected_options,
      discount: form.discount,
      discount_type: form.discount_type,
      notes: form.notes,
      validity_days: form.validity_days,
      show_snc: form.show_snc,
      show_financement: form.show_financement || (form.banques || []).length > 0,
      cabinet_snc_id: selectedCabinetSNC?.id || null,
      cabinet_snc_name: selectedCabinetSNC?.name || null,
      cabinet_snc_email: selectedCabinetSNC?.email || null,
      banques: form.banques || [],
    };
    
    try {
      await devisAPI.update(id, devisData);
      toast.success('Devis mis à jour');
      navigate(`/admin/devis/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (form.devis_type === 'service') {
    return (
      <ServiceQuoteEditor
        title="Modifier le devis prestation"
        subtitle="Prestations atelier, peinture, entretien, réparation et pièces"
        reference={originalDevis?.reference}
        clients={clients}
        serviceCatalog={serviceCatalog}
        form={form}
        setForm={setForm}
        onBack={() => navigate(`/admin/devis/${id}`)}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel="Enregistrer"
      />
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="devis-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/devis/${id}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              Modifier le devis
            </h1>
            <p className="text-slate-500 mt-1">
              {originalDevis?.reference}
            </p>
          </div>
        </div>
          <div className="flex items-center gap-4">
          {/* SNC Toggle */}
          <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg border border-yellow-200">
            <Calculator className="w-4 h-4 text-yellow-600" />
            <Label htmlFor="snc-toggle" className="text-sm font-medium text-yellow-700">
              Simulation SNC
            </Label>
            <Switch
              id="snc-toggle"
              checked={form.show_snc}
              onCheckedChange={(checked) => setForm({ ...form, show_snc: checked, cabinet_snc_id: checked ? form.cabinet_snc_id : '' })}
              data-testid="snc-toggle"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
            <Banknote className="w-4 h-4 text-blue-600" />
            <Label htmlFor="financing-toggle" className="text-sm font-medium text-blue-700">
              Mention banque
            </Label>
            <Switch
              id="financing-toggle"
              checked={form.show_financement}
              onCheckedChange={(checked) => setForm({ ...form, show_financement: checked, banques: checked ? form.banques : [] })}
              data-testid="financing-toggle"
            />
          </div>
          {/* Cabinet SNC selector */}
          {form.show_snc && cabinetsSNC.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-yellow-600" />
              <Select
                value={form.cabinet_snc_id || "none"}
                onValueChange={(value) => setForm({ ...form, cabinet_snc_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="w-48 h-9 border-yellow-200 bg-yellow-50" data-testid="cabinet-snc-select">
                  <SelectValue placeholder="Cabinet SNC (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun cabinet</SelectItem>
                  {cabinetsSNC.map((cabinet) => (
                    <SelectItem key={cabinet.id} value={cabinet.id}>
                      {cabinet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Banques selector */}
          {form.show_financement && (
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <Banknote className="w-4 h-4 text-blue-600" />
                <Label className="font-medium text-slate-800">Banques partenaires (sélection multiple)</Label>
              </div>
              <p className="text-xs text-slate-500">
                Les banques sélectionnées recevront le devis en copie par email, même sans simulation SNC.
              </p>
              {banques.length === 0 ? (
                <p className="text-sm text-slate-500 italic">
                  Aucune banque configurée. Créez un contact avec le type "Banque" dans la section Contacts.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {banques.map((banque) => {
                    const isSelected = form.banques?.some((b) => b.id === banque.id);
                    return (
                      <button
                        type="button"
                        key={banque.id}
                        onClick={() => toggleBanqueSelection(banque)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all text-left',
                          isSelected
                            ? 'border-yellow-500 bg-yellow-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        )}
                        data-testid={`banque-option-${banque.id}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleBanqueSelection(banque)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{banque.name}</p>
                          {banque.email && (
                            <p className="text-xs text-slate-500">{banque.email}</p>
                          )}
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-yellow-600" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {form.banques?.length > 0 && (
                <div className="mt-2 p-2 bg-yellow-100 rounded text-sm">
                  <span className="font-medium text-yellow-800">
                    {form.banques.length} banque(s) sélectionnée(s)
                  </span>
                  <span className="text-yellow-700"> - recevront le devis en CC</span>
                </div>
              )}
            </div>
          )}
          
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
            data-testid="save-devis-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Save className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Selection */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Users className="w-5 h-5 text-yellow-500" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <Select
                value={form.client_id}
                onValueChange={(value) => setForm({ ...form, client_id: value })}
              >
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Choisir un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} {client.company && `(${client.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedClient && (
                <div className="mt-3 p-3 bg-slate-50 rounded-md">
                  <p className="font-medium text-slate-800">{selectedClient.name}</p>
                  {selectedClient.company && <p className="text-slate-500">{selectedClient.company}</p>}
                  {selectedClient.email && <p className="text-sm text-slate-500">{selectedClient.email}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Machine Selection */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Barlow Condensed' }}>
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-yellow-500" />
                  Machine
                </div>
                <div className="flex items-center gap-2">
                  {/* Brand filter */}
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger className="w-[140px] h-8 text-xs">
                      <SelectValue placeholder="Marque" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      {[...new Set(miniPelles.map(mp => mp.brand).filter(Boolean))].sort().map(brand => (
                        <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* View toggle */}
                  <div className="flex items-center gap-1 border rounded-lg p-1 bg-white">
                    <Button
                      variant={machineViewMode === 'dropdown' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMachineViewMode('dropdown')}
                      className={machineViewMode === 'dropdown' ? 'bg-slate-800 h-7' : 'h-7'}
                      title="Liste déroulante"
                    >
                      <Search className="w-3 h-3" />
                    </Button>
                    <Button
                      variant={machineViewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMachineViewMode('grid')}
                      className={machineViewMode === 'grid' ? 'bg-slate-800 h-7' : 'h-7'}
                      title="Grille"
                    >
                      <LayoutGrid className="w-3 h-3" />
                    </Button>
                    <Button
                      variant={machineViewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMachineViewMode('list')}
                      className={machineViewMode === 'list' ? 'bg-slate-800 h-7' : 'h-7'}
                      title="Liste"
                    >
                      <List className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {/* Dropdown View - Quick Selection */}
              {machineViewMode === 'dropdown' && (
                <div className="space-y-4">
                  <div className="bg-slate-50 border rounded-lg p-4">
                    <Label className="text-sm text-slate-600 mb-2 block">Sélection rapide par liste déroulante</Label>
                    <Popover open={machinePopoverOpen} onOpenChange={setMachinePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={machinePopoverOpen}
                          className="w-full justify-between bg-white font-normal h-10"
                        >
                          {form.mini_pelle_id
                            ? (() => {
                                const mp = miniPelles.find(m => m.id === form.mini_pelle_id);
                                const t = mp && formatTonnage(mp);
                                return mp ? `${mp.name}${t ? ' — ' + t : ''}` : 'Choisir une machine...';
                              })()
                            : 'Choisir une machine...'}
                          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 flex-shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[420px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Rechercher par nom, marque, tonnage…" className="h-9" />
                          <CommandList className="max-h-[340px]">
                            <CommandEmpty>Aucune machine trouvée.</CommandEmpty>
                            {[...new Set(miniPelles.map(mp => mp.brand || 'Autres'))].sort().map(brand => (
                              <CommandGroup key={brand} heading={brand}>
                                {miniPelles.filter(mp => (mp.brand || 'Autres') === brand).map((mp) => {
                                  const tonnage = formatTonnage(mp);
                                  return (
                                    <CommandItem
                                      key={mp.id}
                                      value={`${mp.name} ${mp.brand || ''} ${tonnage || ''} ${mp.weight || ''}`}
                                      onSelect={() => {
                                        setForm({ ...form, mini_pelle_id: mp.id, selected_variant_id: '', selected_variant_price: null, selected_options: [] });
                                        setMachinePopoverOpen(false);
                                      }}
                                      className="flex items-center justify-between gap-2 py-2.5 cursor-pointer"
                                    >
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <Check className={`w-4 h-4 flex-shrink-0 ${form.mini_pelle_id === mp.id ? 'opacity-100 text-green-600' : 'opacity-0'}`} />
                                        <span className="font-medium truncate">{mp.name}</span>
                                        {tonnage && (
                                          <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono flex-shrink-0">{tonnage}</span>
                                        )}
                                      </div>
                                      <span className="text-yellow-600 font-mono text-sm flex-shrink-0">{formatPrice(mp.base_price)}</span>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* Selected machine preview */}
                  {selectedMiniPelle && (
                    <div className="border-2 border-yellow-500 rounded-lg p-4 bg-yellow-50">
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0">
                          {getImageUrl(selectedMiniPelle.image_url) ? (
                            <img src={getImageUrl(selectedMiniPelle.image_url)} alt={selectedMiniPelle.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          {selectedMiniPelle.brand && (
                            <p className="text-xs text-yellow-600 font-semibold">{selectedMiniPelle.brand}</p>
                          )}
                          <h3 className="font-bold text-lg text-slate-800">{selectedMiniPelle.name}</h3>
                          <p className="text-sm text-slate-500">{selectedMiniPelle.model}</p>
                          <p className="font-mono text-xl font-bold text-yellow-600 mt-1">
                            {formatPrice(effectivePrice)}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Check className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Brand badges - only show for grid/list views */}
              {machineViewMode !== 'dropdown' && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge 
                    variant={selectedBrand === 'all' ? 'default' : 'outline'}
                    className={`cursor-pointer ${selectedBrand === 'all' ? 'bg-yellow-500' : ''}`}
                    onClick={() => setSelectedBrand('all')}
                  >
                    Toutes ({miniPelles.length})
                  </Badge>
                  {[...new Set(miniPelles.map(mp => mp.brand).filter(Boolean))].sort().map(brand => (
                    <Badge 
                      key={brand}
                      variant={selectedBrand === brand ? 'default' : 'outline'}
                      className={`cursor-pointer ${selectedBrand === brand ? 'bg-yellow-500' : ''}`}
                      onClick={() => setSelectedBrand(brand)}
                    >
                      {brand} ({miniPelles.filter(mp => mp.brand === brand).length})
                    </Badge>
                  ))}
                </div>
              )}
              
              {machineViewMode !== 'dropdown' && (() => {
                const filteredMachines = selectedBrand === 'all' 
                  ? miniPelles 
                  : miniPelles.filter(mp => mp.brand === selectedBrand);
                
                return (
                  <>
              {/* Grid View */}
              {machineViewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMachines.map((mp) => (
                  <div
                    key={mp.id}
                    onClick={() => setForm({ ...form, mini_pelle_id: mp.id, selected_variant_id: '', selected_variant_price: null })}
                    className={cn(
                      'border rounded-md p-3 cursor-pointer transition-all',
                      form.mini_pelle_id === mp.id
                        ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
                        : 'border-slate-200 hover:border-slate-300'
                    )}
                    data-testid={`select-mp-${mp.id}`}
                  >
                    <div className="aspect-video bg-slate-100 rounded-md mb-2 overflow-hidden">
                      {getImageUrl(mp.image_url) ? (
                        <img src={getImageUrl(mp.image_url)} alt={mp.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-slate-300" />
                        </div>
                      )}
                    </div>
                    {mp.brand && <p className="text-xs text-yellow-600 font-semibold">{mp.brand}</p>}
                    <h3 className="font-semibold text-slate-800 text-sm">{mp.name}</h3>
                    <p className="font-mono text-yellow-600 font-bold">{formatPrice(mp.base_price)}</p>
                  </div>
                ))}
              </div>
              )}

              {/* List View */}
              {machineViewMode === 'list' && (
              <div className="space-y-3">
                {filteredMachines.map((mp) => (
                  <div
                    key={mp.id}
                    onClick={() => setForm({ ...form, mini_pelle_id: mp.id, selected_variant_id: '', selected_variant_price: null })}
                    className={cn(
                      'border rounded-lg p-3 cursor-pointer transition-all',
                      form.mini_pelle_id === mp.id
                        ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
                        : 'border-slate-200 hover:border-slate-300'
                    )}
                    data-testid={`select-mp-list-${mp.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-16 sm:w-24 sm:h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {getImageUrl(mp.image_url) ? (
                          <img src={getImageUrl(mp.image_url)} alt={mp.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                              {mp.name}
                            </h3>
                            <p className="text-sm text-slate-500">{mp.model}</p>
                          </div>
                          <p className="font-mono text-lg font-bold text-yellow-600 flex-shrink-0">
                            {formatPrice(mp.base_price)}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                          {mp.weight && <span className="bg-slate-100 px-2 py-0.5 rounded">{mp.weight}</span>}
                          {mp.power && <span className="bg-slate-100 px-2 py-0.5 rounded hidden sm:inline">{mp.power}</span>}
                        </div>
                      </div>
                      {form.mini_pelle_id === mp.id && (
                        <div className="flex-shrink-0">
                          <div className="w-7 h-7 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
                  </>
                );
              })()}

              {/* Variant selector */}
              {selectedMiniPelle?.has_variants && selectedMiniPelle.variants?.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    Choisir une variante pour {selectedMiniPelle.name}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedMiniPelle.variants.map((variant) => (
                      <div
                        key={variant.id}
                        onClick={() => setForm({ ...form, selected_variant_id: variant.id, selected_variant_price: variant.price })}
                        className={cn(
                          'border rounded-lg p-3 cursor-pointer transition-all',
                          form.selected_variant_id === variant.id
                            ? 'border-yellow-500 bg-white ring-2 ring-yellow-500'
                            : 'border-slate-200 bg-white hover:border-yellow-300'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {variant.image_url && (
                            <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                              <img src={getImageUrl(variant.image_url)} alt={variant.name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800">{variant.name}</p>
                            <p className="font-mono text-yellow-600 font-bold">{formatPrice(variant.price)}</p>
                          </div>
                          {form.selected_variant_id === variant.id && (
                            <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Options */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Wrench className="w-5 h-5 text-yellow-500" />
                Options et Accessoires
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4">
                {categories.map((cat, catIndex) => {
                  const catOptions = options.filter((o) => o.category === cat.id);
                  if (catOptions.length === 0) return null;
                  
                  // Couleurs alternées pour les catégories
                  const categoryColors = [
                    { border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
                    { border: 'border-l-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
                    { border: 'border-l-violet-500', bg: 'bg-violet-50', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700' },
                    { border: 'border-l-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700' },
                    { border: 'border-l-pink-500', bg: 'bg-pink-50', text: 'text-pink-700', badge: 'bg-pink-100 text-pink-700' },
                    { border: 'border-l-cyan-500', bg: 'bg-cyan-50', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700' },
                  ];
                  const colorSet = categoryColors[catIndex % categoryColors.length];
                  
                  return (
                    <div 
                      key={cat.id} 
                      className={cn(
                        "rounded-lg border border-slate-200 overflow-hidden",
                        "border-l-4",
                        colorSet.border
                      )}
                    >
                      {/* Category Header */}
                      <div className={cn("px-4 py-2.5 flex items-center justify-between", colorSet.bg)}>
                        <h4 className={cn("font-semibold flex items-center gap-2 text-sm", colorSet.text)}>
                          <Wrench className="w-4 h-4" />
                          {cat.name}
                        </h4>
                        <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", colorSet.badge)}>
                          {catOptions.length} option{catOptions.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {/* Category Options */}
                      <div className="p-3 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {catOptions.map((opt) => {
                            const price = getOptionPrice(opt);
                            const isSelected = form.selected_options.some((o) => o.option_id === opt.id);
                            
                            return (
                              <div
                                key={opt.id}
                                className={cn(
                                  'flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-all',
                                  isSelected
                                    ? 'border-yellow-500 bg-yellow-50 shadow-sm'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                )}
                                onClick={() => toggleOption(opt)}
                                data-testid={`toggle-opt-${opt.id}`}
                              >
                                <Checkbox checked={isSelected} className="flex-shrink-0" />
                                {getImageUrl(opt.image_url) ? (
                                  <img
                                    src={getImageUrl(opt.image_url)}
                                    alt={opt.name}
                                    className="w-10 h-10 object-cover rounded flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                                    <Wrench className="w-4 h-4 text-slate-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-800 text-sm truncate">{opt.name}</p>
                                </div>
                                <span className={cn(
                                  "font-mono text-sm flex-shrink-0",
                                  price <= 0 ? "text-green-600" : "text-slate-700"
                                )}>
                                  {price > 0 ? formatPrice(price) : '✓ Inclus'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Notes & Discount */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                Conditions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Remise</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.discount}
                    onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })}
                    data-testid="discount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={form.discount_type}
                    onValueChange={(value) => setForm({ ...form, discount_type: value })}
                  >
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">%</SelectItem>
                      <SelectItem value="fixed">€</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Validité (jours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.validity_days}
                    onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 30 })}
                    data-testid="validity-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="Notes internes ou conditions particulières..."
                  data-testid="notes-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Totals */}
        <div>
          <Card className="border-slate-200 sticky top-4">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Récapitulatif</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-3">
              <div className="flex justify-between text-slate-600">
                <span>Machine</span>
                <span className="font-mono">{formatPrice(totals.miniPellePrice)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Options ({form.selected_options.length})</span>
                <span className="font-mono">{formatPrice(totals.optionsTotal)}</span>
              </div>
              <div className="border-t border-slate-200 pt-3 flex justify-between">
                <span className="text-slate-600">Sous-total</span>
                <span className="font-mono font-medium">{formatPrice(totals.subtotal)}</span>
              </div>
              {form.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Remise ({form.discount}{form.discount_type === 'percent' ? '%' : '€'})</span>
                  <span className="font-mono">-{formatPrice(totals.discountAmount)}</span>
                </div>
              )}
              <div className="border-t border-slate-200 pt-3 flex justify-between">
                <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                  TOTAL TTC
                </span>
                <span className="text-xl font-mono font-bold text-yellow-600">
                  {formatPrice(totals.total)}
                </span>
              </div>
              
              {/* Simulation SNC */}
              {form.show_snc && totals.total > 0 && (
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Calculator className="w-3 h-3" />
                      Simulation SNC
                    </p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Apport (30%)</span>
                        <span className="font-mono text-slate-800">{formatPrice(totals.total * 0.30)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">À financer</span>
                        <span className="font-mono text-slate-800">{formatPrice(totals.total * 0.70)}</span>
                      </div>
                      <div className="flex justify-between font-semibold bg-yellow-100 -mx-3 px-3 py-2 rounded mt-2">
                        <span className="text-yellow-700">Loyer 60 mois</span>
                        <span className="font-mono text-yellow-600">{formatPrice((totals.total * 0.70 / 60) * 1.2)}/mois</span>
                      </div>
                    </div>
                    {/* Cabinet SNC selection info */}
                    {selectedCabinetSNC && (
                      <div className="mt-3 pt-3 border-t border-yellow-200 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-700">
                          Cabinet: <strong>{selectedCabinetSNC.name}</strong>
                        </span>
                        <span className="text-xs text-yellow-600">(sera en copie de l'email)</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DevisEditPage;
